import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { Row, Col, Card, Button, Table, Spinner, Badge, Form } from 'react-bootstrap';
import { LuTrash2, LuPlus, LuMinus, LuShoppingCart, LuArrowRight, LuArrowLeft, LuBox } from "react-icons/lu";
import { updateCartItem, removeCartItem, clearCart } from '../../redux/reducers/portalCartSlice';
import { formatCurrency } from '../../utils/currency';

const PortalCart = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { businessId } = useParams();
    const { cart, loading } = useSelector((state) => state.portalCart);
    const currency = useSelector((state) => state.portalAuth?.businessData?.jssettings?.currency || 'CAD');

    if (loading && !cart?.items?.length) return <div className="text-center py-5"><Spinner animation="border" style={{ color: "#28B0C4" }} /></div>;

    if (!cart?.items?.length) {
        return (
            <div className="text-center py-5">
                <div className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center mb-4" style={{ width: '100px', height: '100px' }}>
                    <LuShoppingCart size={48} className="text-muted opacity-50" />
                </div>
                <h3 className="fw-bold">Your cart is empty</h3>
                <p className="text-muted mb-4">Browse our catalog and add items to your cart to get started.</p>
                <Button as={Link} to={`/customer-side/${businessId}/products`} className="rounded-3 px-4 py-2 text-white border-0" style={{ background: "#28B0C4" }}>
                    Browse Products
                </Button>
            </div>
        );
    }

    return (
        <div>
            <h2 className="fw-bold text-dark mb-4">Shopping Cart</h2>

            <Row className="g-4">
                <Col lg={8}>
                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table className="align-middle mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4 py-3 text-muted small fw-bold">PRODUCT</th>
                                            <th className="py-3 text-muted small fw-bold text-center">QUANTITY</th>
                                            <th className="py-3 text-muted small fw-bold">TOTAL</th>
                                            <th className="pe-4 py-3"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cart.items.map((item) => (
                                            <tr key={item.productId}>
                                                <td className="ps-4 py-3">
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div className="rounded-3 bg-light overflow-hidden" style={{ width: '60px', height: '60px', flexShrink: 0 }}>
                                                            {item.productImage ? (
                                                                <img src={item.productImage} alt={item.productName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                            ) : (
                                                                <div className="w-100 h-100 d-flex align-items-center justify-content-center text-muted opacity-25">
                                                                    <LuBox size={24} />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <div className="fw-bold text-dark text-truncate" style={{ maxWidth: '200px' }}>{item.productName}</div>
                                                            <div className="text-muted small">{formatCurrency(item.unitPrice, currency)} / unit</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-3">
                                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                                        <Button 
                                                            variant="light" 
                                                            size="sm" 
                                                            className="rounded-2 p-1"
                                                            onClick={() => dispatch(updateCartItem({ productId: item.productId, quantity: Math.max(1, item.quantity - 1) }))}
                                                        >
                                                            <LuMinus size={14} />
                                                        </Button>
                                                        <Form.Control
                                                            type="number"
                                                            className="text-center fw-bold bg-transparent border-0 px-1 shadow-none"
                                                            style={{ width: '60px' }}
                                                            value={item.quantity}
                                                            onChange={(e) => {
                                                                const val = parseInt(e.target.value);
                                                                if (!isNaN(val)) {
                                                                    dispatch(updateCartItem({ productId: item.productId, quantity: Math.max(1, val) }));
                                                                }
                                                            }}
                                                            onBlur={(e) => {
                                                                const val = parseInt(e.target.value);
                                                                if (isNaN(val) || val < 1) {
                                                                    dispatch(updateCartItem({ productId: item.productId, quantity: 1 }));
                                                                }
                                                            }}
                                                            min="1"
                                                         />
                                                        <Button 
                                                            variant="light" 
                                                            size="sm" 
                                                            className="rounded-2 p-1"
                                                            onClick={() => dispatch(updateCartItem({ productId: item.productId, quantity: item.quantity + 1 }))}
                                                        >
                                                            <LuPlus size={14} />
                                                        </Button>
                                                    </div>
                                                </td>
                                                <td className="py-3 fw-bold text-dark">
                                                    {formatCurrency(item.unitPrice * item.quantity, currency)}
                                                </td>
                                                <td className="pe-4 py-3 text-end">
                                                    <Button 
                                                        variant="link" 
                                                        className="text-danger p-0"
                                                        onClick={() => dispatch(removeCartItem(item.productId))}
                                                    >
                                                        <LuTrash2 size={18} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                            <div className="p-3 border-top bg-light d-flex justify-content-between">
                                <Button variant="link" className="text-danger text-decoration-none small p-0 fw-semibold" onClick={() => dispatch(clearCart())}>
                                    Clear Cart
                                </Button>
                                <Button as={Link} to={`/customer-side/${businessId}/products`} variant="link" className="text-dark text-decoration-none small p-0 fw-semibold d-flex align-items-center gap-1">
                                    <LuPlus size={14} /> Add More Items
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={4}>
                    <Card className="border-0 shadow-sm rounded-4 sticky-top" style={{ top: '100px' }}>
                        <Card.Body className="p-4">
                            <h5 className="fw-bold mb-4">Order Summary</h5>
                            
                            <div className="d-flex justify-content-between mb-2 text-muted">
                                <span>Subtotal</span>
                                <span>{formatCurrency(cart.totals?.subtotal, currency)}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2 text-muted">
                                <span>Items</span>
                                <span>{cart.totals?.itemCount}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-4 text-muted">
                                <span>Shipping</span>
                                <span className="text-success small fw-semibold">Calculated at checkout</span>
                            </div>
                            
                            <hr className="mb-4" />
                            
                            <div className="d-flex justify-content-between mb-4">
                                <h5 className="fw-bold">Total</h5>
                                <h5 className="fw-bold" style={{ color: "#28B0C4" }}>{formatCurrency(cart.totals?.subtotal, currency)}</h5>
                            </div>
                            
                            <Button 
                                className="w-100 rounded-3 py-3 fw-bold text-white border-0 d-flex align-items-center justify-content-center gap-2 shadow-sm"
                                style={{ background: "#28B0C4" }}
                                onClick={() => navigate(`/customer-side/${businessId}/checkout`)}
                            >
                                Proceed to Checkout <LuArrowRight size={18} />
                            </Button>
                            
                            <Button 
                                variant="link" 
                                className="w-100 text-muted small mt-3 text-decoration-none d-flex align-items-center justify-content-center gap-1"
                                onClick={() => navigate(`/customer-side/${businessId}/products`)}
                            >
                                <LuArrowLeft size={14} /> Continue Shopping
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default PortalCart;
